import { PreviewSheet } from '../../PreviewSheet';
import { ExternalLinkIcon } from '../../icons';
import { PriceModifierOverviewSection } from './PriceModifierOverviewSection';
import { KIND_INFO } from './priceModifierTypes';
import type { PriceModifierKind, PriceModifierRecord } from './priceModifierTypes';
import { FooterEditLink } from '../../common/FooterEditLink';
import { useTranslation } from '@hooks/useTranslation';

export type { PriceModifierKind, PriceModifierRecord };

interface PriceModifierDetailSheetProps {
  record: PriceModifierRecord | null;
  kind: PriceModifierKind | null;
  open: boolean;
  // RBAC: hide the footer Edit button for read-only users.
  showEdit?: boolean;
  onOpenChange: (open: boolean) => void;
}

function SheetHeader({
  record,
  kind,
  onClose,
}: {
  record: PriceModifierRecord;
  kind: PriceModifierKind;
  onClose: () => void;
}) {
  const info = KIND_INFO[kind];
  const label = `View ${info.noun} details`;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-3">
        <a
          href={`/${info.pluralPath}/${record.id}`}
          onClick={onClose}
          className="text-grey hover:text-off-black transition-colors"
          aria-label={label}
        >
          <ExternalLinkIcon />
        </a>
        <span className="font-inter text-lg font-semibold text-off-black">{label}</span>
      </div>
    </div>
  );
}

export function PriceModifierDetailSheet({
  record,
  kind,
  open,
  showEdit = true,
  onOpenChange,
}: PriceModifierDetailSheetProps) {
  const { t } = useTranslation();
  const ready = record !== null && kind !== null;

  return (
    <PreviewSheet
      open={open && ready}
      onOpenChange={onOpenChange}
      header={ready ? <SheetHeader record={record} kind={kind} onClose={() => onOpenChange(false)} /> : undefined}
      footerClassName="justify-end"
      footer={ready && showEdit ? <FooterEditLink href={`/${KIND_INFO[kind].pluralPath}/${record.id}/edit`} label={t('common.footer.edit')} /> : undefined}
    >
      {ready && <PriceModifierOverviewSection record={record} kind={kind} />}
    </PreviewSheet>
  );
}
